import React, { useState, createContext, useContext } from 'react'
import { useFetchWithAuth } from '../../hooks/useFetchWithAuth'
import { useAuth } from '../auth-context/AuthContext'
import { apiResponse, createDownloadLink, ResponseType, showSnackbar } from '../../utils/Utils'
import { DataType } from '../../lib/MapLib'
import { VariantType } from '../../lib/CommonLib'

interface DataPointViewContextType {
  fetchDatapoint: (dataType: string) => void
  selectedDataType: string
  setSelectedDataType: (val: string) => void
  listStationsDetails: any
  stationFilter: string
  setStationFilter: (val: string) => void
  downLoadDataPoint: (val: string) => void
}

const DataPointContext = createContext<DataPointViewContextType | undefined>(undefined)

export const DataPointViewProvider = ({ children }: { children: React.ReactNode }) => {
  const { setSnackbarOpen, setSnackbarMessage, setVariant, setLoading } = useAuth()
  const [stationFilter, setStationFilter] = useState<string>('')
  const [selectedDataType, setSelectedDataType] = useState<string>(DataType.WaterQuality)
  const [listStationsDetails, setListStationsDetails] = useState<any>([])
  const fetchWithAuth = useFetchWithAuth()

  const fetchDatapoint = async (dataType: string) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth(`/api/DataPoints?dataType=${dataType}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const data = await apiResponse(response)
      setListStationsDetails(data)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  const downLoadDataPoint = async (dataType: string) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth(`/api/DataPoints/download?dataType=${dataType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      let filename = `${dataType}_data_point`
      const blob = await apiResponse(response, ResponseType.blob)
      const downloadUrl = window.URL.createObjectURL(blob)
      createDownloadLink(downloadUrl, filename)
      window.URL.revokeObjectURL(downloadUrl)
      showSnackbar(
        'Data point successfully downloaded',
        VariantType.success,
        setVariant,
        setSnackbarOpen,
        setSnackbarMessage
      )
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <DataPointContext.Provider
      value={{
        fetchDatapoint,
        selectedDataType,
        setSelectedDataType,
        listStationsDetails,
        stationFilter,
        setStationFilter,
        downLoadDataPoint,
      }}
    >
      {children}
    </DataPointContext.Provider>
  )
}

export const useDataPointView = () => {
  const context = useContext(DataPointContext)
  if (context === undefined) {
    throw new Error('useDataPointView must be used within a DataPointViewProvider')
  }
  return context
}
